import {
  LitElement, html, nothing,
  type CSSResultGroup, type PropertyValues, type TemplateResult,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';

import styles from './styles.js';
import type { CalendarCell, MonthGrid } from './types.js';
import { CalendarController }   from './calendar.controller.js';
import { SelectionController }  from './selection.controller.js';
import { FormatController }     from './format.controller.js';
import { TimeController }       from './time.controller.js';

import type {
  Placement,
  Size,
  DateSelectionMode,
  DatePickerView,
  HourFormat,
  DatePickerValue,
  DatePickerSingleValue,
  DatePickerRangeValue,
  DateMonthChangeEventDetail,
} from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController }  from '../utils/placement.controller.js';
import { FormAssociatedMixin }  from '../utils/form-associated.mixin.js';

export type DatepickerSize      = Size;
export type DatepickerVariant   = 'outlined' | 'filled';
export type DatepickerFloatLabel = 'over' | 'in' | 'on';

let _dpPanelIdCount = 0;

/**
 * uwc-datepicker
 *
 * Full-featured date picker. Supports single, range, and multiple selection,
 * month/year picker views, inline and popup modes, time picker, and full
 * keyboard + screen-reader accessibility.
 *
 * @attr value              - Bound Date value (DatePickerValue).
 * @attr selection-mode     - 'single' | 'range' | 'multiple'. Default: single.
 * @attr view               - 'date' | 'month' | 'year'. Default: date.
 * @attr date-format        - PrimeNG-style format string. Default: mm/dd/yy.
 * @attr locale             - BCP-47 locale for month/day names. Default: en.
 * @attr min-date           - Minimum selectable Date.
 * @attr max-date           - Maximum selectable Date.
 * @attr disabled-dates     - Array of Date objects to disable.
 * @attr disabled-days      - Array of day-of-week indices (0=Sun) to disable.
 * @attr show-icon          - Show calendar icon button next to input.
 * @attr icon               - Icon name. Default: calendar.
 * @attr show-clear         - Show clear (×) button when a value is set.
 * @attr show-button-bar    - Show Today / Clear footer buttons.
 * @attr show-time          - Enable time picker section.
 * @attr time-only          - Show only the time picker (no date grid).
 * @attr hour-format        - '12' | '24'. Default: 24.
 * @attr show-seconds       - Show seconds spinner in time picker.
 * @attr number-of-months   - Number of calendar months to display. Default: 1.
 * @attr inline             - Always display calendar (no popup trigger).
 * @attr placeholder        - Input placeholder text.
 * @attr label              - Accessible label for the input.
 * @attr disabled           - Disable the component.
 * @attr readonly           - Prevent user input (value still readable).
 * @attr required           - Mark field as required for form validation.
 * @attr invalid            - Apply error styling.
 * @attr fluid              - Stretch to full container width.
 * @attr size               - 'small' | 'medium' | 'large'. Default: medium.
 * @attr variant            - 'outlined' | 'filled'. Default: outlined.
 * @attr placement          - Popup placement. Default: bottom-start.
 * @attr offset             - Gap between trigger and panel (px). Default: 4.
 * @attr first-day-of-week  - 0=Sun … 6=Sat. Default: 0.
 * @attr float-label        - Floating label mode: over | in | on.
 *
 * @property renderDateCell - (date: Date) => TemplateResult | string. Custom cell content.
 *
 * @fires uwc-date-select   - detail: { value, selectionMode, originalEvent }
 * @fires uwc-date-clear    - detail: { previousValue }
 * @fires uwc-month-change  - detail: { month, year } — fired on calendar navigation
 * @fires uwc-show          - Panel opened.
 * @fires uwc-hide          - Panel closed.
 *
 * @csspart trigger         - The trigger input wrapper.
 * @csspart input           - The text input inside the trigger.
 * @csspart panel           - The calendar panel.
 * @csspart header          - Month/year navigation header.
 * @csspart day-cell        - Individual day button.
 *
 * @cssprop --uwc-dp-trigger-bg
 * @cssprop --uwc-dp-trigger-border
 * @cssprop --uwc-dp-trigger-radius
 * @cssprop --uwc-dp-trigger-color
 * @cssprop --uwc-dp-trigger-focus-border
 * @cssprop --uwc-dp-trigger-disabled-bg
 * @cssprop --uwc-dp-placeholder-color
 * @cssprop --uwc-dp-icon-color
 * @cssprop --uwc-dp-panel-bg
 * @cssprop --uwc-dp-panel-border
 * @cssprop --uwc-dp-panel-radius
 * @cssprop --uwc-dp-panel-shadow
 * @cssprop --uwc-dp-nav-color
 * @cssprop --uwc-dp-header-color
 * @cssprop --uwc-dp-cell-color
 * @cssprop --uwc-dp-cell-radius
 * @cssprop --uwc-dp-today-color
 * @cssprop --uwc-dp-selected-bg
 * @cssprop --uwc-dp-selected-color
 * @cssprop --uwc-dp-range-bg
 * @cssprop --uwc-dp-duration
 * @cssprop --uwc-dp-z
 */
export class UwcDatepicker extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = styles;

  // ── PlacementHost required ────────────────────────────────────────
  @property({ reflect: true })                                      placement: Placement  = 'bottom-start';
  @property({ type: Number })                                       offset                = 4;

  // ── Value & selection ─────────────────────────────────────────────
  @property({ attribute: false })                                   value: DatePickerValue               = null;
  @property({ reflect: true, attribute: 'selection-mode' })         selectionMode: DateSelectionMode     = 'single';

  // ── View ──────────────────────────────────────────────────────────
  @property({ reflect: true })                                      view: DatePickerView                 = 'date';
  @property({ type: Number, attribute: 'number-of-months' })        numberOfMonths                       = 1;

  // ── Format ────────────────────────────────────────────────────────
  @property({ attribute: 'date-format' })                           dateFormat                           = 'mm/dd/yy';
  @property()                                                       locale                               = 'en';

  // ── Boundaries ────────────────────────────────────────────────────
  @property({ attribute: false })                                   minDate: Date | null                 = null;
  @property({ attribute: false })                                   maxDate: Date | null                 = null;
  @property({ attribute: false })                                   disabledDates: Date[]                = [];
  @property({ type: Array,   attribute: 'disabled-days' })          disabledDays: number[]               = [];
  @property({ type: Number,  attribute: 'first-day-of-week' })      firstDayOfWeek                       = 0;

  // ── UI flags ──────────────────────────────────────────────────────
  @property({ type: Boolean, reflect: true })                       inline                               = false;
  @property({ type: Boolean, attribute: 'show-icon' })              showIcon                             = false;
  @property({ type: String })                                       icon                                 = 'calendar';
  @property({ type: Boolean, attribute: 'show-clear' })             showClear                            = false;
  @property({ type: Boolean, attribute: 'show-button-bar' })        showButtonBar                        = false;
  @property({ type: Boolean, attribute: 'show-time' })              showTime                             = false;
  @property({ type: Boolean, reflect: true, attribute: 'time-only' }) timeOnly                           = false;
  @property({ reflect: true, attribute: 'hour-format' })            hourFormat: HourFormat               = '24';
  @property({ type: Boolean, attribute: 'show-seconds' })           showSeconds                          = false;
  @property({ type: Boolean, reflect: true })                       disabled                             = false;
  @property({ type: Boolean, reflect: true })                       readonly                             = false;
  @property({ type: Boolean, reflect: true })                       required                             = false;
  @property({ type: Boolean, reflect: true })                       invalid                              = false;
  @property({ type: Boolean, reflect: true })                       fluid                                = false;
  @property({ reflect: true })                                      size: DatepickerSize                 = 'medium';
  @property({ reflect: true })                                      variant: DatepickerVariant           = 'outlined';
  @property()                                                       placeholder                          = '';
  @property()                                                       label                                = '';
  @property({ type: String, attribute: 'float-label', reflect: true }) floatLabel: DatepickerFloatLabel | null = null;

  /** Custom date cell renderer. Receives the cell Date, returns TemplateResult or string. */
  renderDateCell?: (date: Date) => TemplateResult | string;

  /** Custom footer template factory. Receives `{ today, clear }` action callbacks. Replaces the default button bar. */
  footerTemplate?: (actions: { today: () => void; clear: () => void }) => TemplateResult;

  // ── Internal reactive state ───────────────────────────────────────
  @state() private _inputText   = '';
  @state() private _yearBase    = 0;    // decade start year for year-picker page

  // ── Unique panel ID for aria-controls ─────────────────────────────
  private readonly _panelId = `uwc-dp-panel-${++_dpPanelIdCount}`;

  // ── Shadow DOM queries ────────────────────────────────────────────
  @query('.dp-trigger')         private _triggerEl!:    HTMLElement;
  @query('.dp-panel')           private _panelEl!:      HTMLElement;
  @query('.dp-input')           private _inputEl?:      HTMLInputElement;
  @query('[data-dp-focused]')   private _focusCellEl?:  HTMLElement;

  // ── Controllers ───────────────────────────────────────────────────
  private _cal  = new CalendarController(this);
  private _sel  = new SelectionController(this);
  private _fmt  = new FormatController(this);
  private _time = new TimeController(this);
  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panelEl ?? null,

    onTriggerFound: (el) => {
      const onClick = (e: Event) => {
        e.stopPropagation();
        if (this.disabled || this.readonly) return;
        this._pc.toggle();
      };
      const onKeydown = (e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') && !this._pc.open) {
          e.preventDefault();
          this._pc.show();
        }
      };
      el.addEventListener('click',   onClick);
      el.addEventListener('keydown', onKeydown as EventListener);
      return () => {
        el.removeEventListener('click',   onClick);
        el.removeEventListener('keydown', onKeydown as EventListener);
      };
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => {
      emit(this, 'uwc-show');
      // Seed keyboard focus anchor so roving tabindex has a starting point
      if (this._cal.activeView === 'date' && !this.timeOnly) {
        this._cal.focusedDate = this._getInitialFocusDate();
        this.updateComplete.then(() => this._focusActiveCell());
      }
    },

    afterHide: () => {
      emit(this, 'uwc-hide');
      this._cal.focusedDate = null;
      this._inputEl?.focus();
    },

    afterPosition: ({ triggerRect, panelEl }) => {
      panelEl.style.minWidth = `${triggerRect.width}px`;
    },
  });

  // ── Lifecycle ─────────────────────────────────────────────────────

  override firstUpdated(): void {
    if (!this.inline) {
      this._pc.setTriggerElement(this._triggerEl);
    }

    this._sel.syncFromProp(this.value);
    this._internals.setFormValue(this._sel.getFormValue());

    this._yearBase = Math.floor(this._cal.viewYear / 10) * 10;

    if ((this.showTime || this.timeOnly) && this.value instanceof Date) {
      this._time.syncFromDate(this.value);
    }

    this._syncInputText();
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (!this.inline) {
      this._pc.updated(changed as Map<PropertyKey, unknown>, null, null);
    }

    if (changed.has('value')) {
      this._sel.syncFromProp(this.value);
      this._internals.setFormValue(this._sel.getFormValue());
      if ((this.showTime || this.timeOnly) && this.value instanceof Date) {
        this._time.syncFromDate(this.value as Date);
      }
      this._syncInputText();
    }
    if (changed.has('view'))       this._cal.activeView = this.view;
    if (changed.has('numberOfMonths')) this.requestUpdate();
    if (changed.has('required') || changed.has('value')) {
      if (this.required && !this._sel.hasValue()) {
        this._internals.setValidity({ valueMissing: true }, 'Please select a date.', this._inputEl);
      } else {
        this._internals.setValidity({});
      }
    }
  }

  // ── Private helpers ───────────────────────────────────────────────

  private _syncInputText(): void {
    if (this.timeOnly) {
      this._inputText = this._formatTimeString();
      return;
    }
    const datePart = this._fmt.formatInputValue(this.value);
    this._inputText = (this.showTime && datePart)
      ? `${datePart} ${this._formatTimeString()}`
      : datePart;
  }

  private _formatTimeString(): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const h = this.hourFormat === '12' ? this._time.displayHour : this._time.hour;
    let s = `${pad(h)}:${pad(this._time.minute)}`;
    if (this.showSeconds) s += `:${pad(this._time.second)}`;
    if (this.hourFormat === '12') s += ` ${this._time.meridiem}`;
    return s;
  }

  private _onDateClick(cell: CalendarCell, e: Event): void {
    if (cell.disabled) return;

    const prevMonth = this._cal.viewMonth;
    const prevYear  = this._cal.viewYear;

    // When showTime is active, overlay the current time spinner state onto the clicked date
    const dateToSelect = (this.showTime && this.selectionMode === 'single')
      ? this._time.applyToDate(cell.date)
      : cell.date;
    this._sel.select(dateToSelect, e);
    this.value = this._sel.value;

    // Auto-navigate to the other-month day's month
    if (cell.otherMonth) {
      this._cal.viewMonth = cell.date.getMonth();
      this._cal.viewYear  = cell.date.getFullYear();
    }

    this._syncInputText();
    this._internals.setFormValue(this._sel.getFormValue());

    // Emit month-change when navigation happens implicitly
    if (this._cal.viewMonth !== prevMonth || this._cal.viewYear !== prevYear) {
      const detail: DateMonthChangeEventDetail = {
        month: this._cal.viewMonth,
        year:  this._cal.viewYear,
      };
      emit(this, 'uwc-month-change', detail);
    }

    // Auto-close popup on complete selection.
    // Keep panel open when showTime so the user can adjust the time spinners.
    if (!this.inline) {
      if (this.selectionMode === 'single' && !this.showTime) {
        this._pc.hide();
      } else if (this.selectionMode === 'range') {
        const r = this._sel.value as DatePickerRangeValue;
        if (r && r[1] !== null) this._pc.hide();
      }
      // multiple mode: never auto-close
    }
  }

  private _onMonthClick(month: number): void {
    if (this.view === 'month') {
      // Month picker mode — emit as value (first day of month)
      const date = new Date(this._cal.viewYear, month, 1);
      this._sel.select(date);
      this.value = this._sel.value;
      this._syncInputText();
      this._internals.setFormValue(this._sel.getFormValue());
      if (!this.inline && this.selectionMode === 'single') this._pc.hide();
    } else {
      // Drill-down in date mode
      this._cal.setViewMonth(month);
      this._cal.switchView('date');
    }
  }

  private _onYearClick(year: number): void {
    if (this.view === 'year') {
      const date = new Date(year, 0, 1);
      this._sel.select(date);
      this.value = this._sel.value;
      this._syncInputText();
      this._internals.setFormValue(this._sel.getFormValue());
      if (!this.inline && this.selectionMode === 'single') this._pc.hide();
    } else {
      this._cal.setViewYear(year);
      this._cal.switchView('month');
    }
  }

  private _onInputChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    this._inputText = input.value;
    const date = this._fmt.parse(input.value);
    if (date) {
      this._sel.select(date, e);
      this.value = this._sel.value;
      this._cal.viewMonth = date.getMonth();
      this._cal.viewYear  = date.getFullYear();
      this._internals.setFormValue(this._sel.getFormValue());
    }
  }

  private _onTodayClick(): void {
    const today = new Date();
    const cell  = {
      date:       today,
      dayNum:     today.getDate(),
      otherMonth: false,
      today:      true,
      disabled:   false,
    };
    this._onDateClick(cell, new MouseEvent('click'));
    this._cal.viewMonth = today.getMonth();
    this._cal.viewYear  = today.getFullYear();
  }

  private _onClearClick(): void {
    const prev = this.value;
    this._sel.clear();
    this.value = this._sel.value;
    this._syncInputText();
    this._internals.setFormValue(null);
    void prev;
  }

  private _navigateMonth(delta: -1 | 1): void {
    const prevMonth = this._cal.viewMonth;
    const prevYear  = this._cal.viewYear;
    this._cal.navigateMonth(delta);
    if (this._cal.viewMonth !== prevMonth || this._cal.viewYear !== prevYear) {
      const detail: DateMonthChangeEventDetail = {
        month: this._cal.viewMonth,
        year:  this._cal.viewYear,
      };
      emit(this, 'uwc-month-change', detail);
    }
  }

  private _switchToMonthView(): void {
    this._cal.switchView('month');
  }

  private _switchToYearView(): void {
    this._yearBase = Math.floor(this._cal.viewYear / 10) * 10;
    this._cal.switchView('year');
  }

  // ── Render methods ────────────────────────────────────────────────

  protected override render(): TemplateResult {
    const panelOpen = this._pc.open || this.inline;

    return html`
      <div class="dp-root">
        ${!this.inline ? this._renderTrigger(panelOpen) : nothing}
        ${!this.inline && this.floatLabel && this.label
          ? this._renderFloatLabel(panelOpen)
          : nothing}
      </div>
      <div
        id=${this._panelId}
        class=${classMap({ 'dp-panel': true, 'is-open': panelOpen })}
        part="panel"
        popover="manual"
        data-placement=${this.placement}
        aria-label="Date picker"
        role=${this.inline ? 'region' : 'dialog'}
        aria-modal=${!this.inline ? 'true' : 'false'}
      >
        ${this._renderPanelContent()}
      </div>
    `;
  }

  private _renderFloatLabel(open: boolean): TemplateResult {
    const active = this._sel.hasValue() || open;
    return html`
      <label
        class=${classMap({
          'dp-float-label':         true,
          'dp-float-label--active': active,
        })}
        aria-hidden="true"
      >${this.label}</label>
    `;
  }

  private _renderTrigger(open: boolean): TemplateResult {
    const hasValue = this._sel.hasValue();
    return html`
      <div
        class=${classMap({
          'dp-trigger':        true,
          'dp-trigger--open':  open,
          'dp-trigger--empty': !hasValue,
        })}
        part="trigger"
      >
        <input
          class="dp-input"
          part="input"
          type="text"
          .value=${this._inputText}
          placeholder=${this.timeOnly
            ? (this.hourFormat === '12' ? '--:-- AM' : '--:--')
            : (this._fmt.getInputPlaceholder() || this.placeholder || 'mm/dd/yyyy')}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          role="combobox"
          aria-haspopup="dialog"
          aria-expanded=${open ? 'true' : 'false'}
          aria-controls=${this._panelId}
          aria-label=${(this.floatLabel && this.label) ? nothing : (this.label || 'Select date')}
          aria-disabled=${this.disabled}
          aria-required=${this.required}
          aria-invalid=${this.invalid}
          autocomplete="off"
          @change=${this._onInputChange}
        />
        ${this.showClear
          ? html`<button
                class="dp-icon--btn"
                type="button"
                tabindex="-1"
                aria-label="Clear date"
                style=${!hasValue ? 'visibility:hidden;pointer-events:none' : ''}
                @click=${(e: Event) => { e.stopPropagation(); if (hasValue) this._onClearClick(); }}
              >
                <uwc-icon name="close" size="1.15em"></uwc-icon>
            </button>`
          : nothing}
        ${this.showIcon ? html`
          <button
            class="dp-icon--btn"
            type="button"
            tabindex="-1"
            aria-hidden="true"
            ?disabled=${this.disabled}
            @click=${(e: Event) => { e.stopPropagation(); if (!this.disabled && !this.readonly) this._pc.toggle(); }}
          >
            <uwc-icon name=${this.icon} size="1.15em"></uwc-icon>
          </button>
        ` : nothing}
      </div>
    `;
  }

  private _renderPanelContent(): TemplateResult {
    if (this.timeOnly) return this._renderTimePicker();

    const activeView = this._cal.activeView;
    if (activeView === 'month') return this._renderMonthPicker();
    if (activeView === 'year')  return this._renderYearPicker();

    const grids = this._cal.getMonthGrids();
    return html`
      <div class="dp-months">
        ${repeat(grids, (g) => `${g.year}-${g.month}`, (grid, idx) => this._renderCalendar(grid, idx, grids.length))}
      </div>
      ${this.showTime ? this._renderTimePicker() : nothing}
      ${this.footerTemplate
      ? html`<div class="dp-button-bar">${this.footerTemplate({ today: () => this._onTodayClick(), clear: () => this._onClearClick() })}</div>`
      : this.showButtonBar
      ? this._renderButtonBar()
      : nothing}
    `;
  }

  private _renderCalendar(grid: MonthGrid, idx: number, total: number): TemplateResult {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const isFirst = idx === 0;
    const isLast  = idx === total - 1;

    return html`
      <div class="dp-cal">
        ${this._renderHeader(grid, monthNames, isFirst, isLast)}
        ${this._renderWeekdays()}
        ${this._renderWeeks(grid)}
      </div>
    `;
  }

  private _renderHeader(
    grid: MonthGrid,
    monthNames: string[],
    showPrev: boolean,
    showNext: boolean,
  ): TemplateResult {
    return html`
      <div class="dp-header" part="header" role="group" aria-label="Calendar navigation">
        <button
          class="dp-nav-btn"
          type="button"
          aria-label="Previous month"
          ?hidden=${!showPrev}
          @click=${() => this._navigateMonth(-1)}
        ><uwc-icon name="chevron-left" size="1.15em"></uwc-icon></button>

        <div class="dp-header-title" aria-live="polite">
          <button
            class="dp-month-label"
            type="button"
            aria-label="Select month"
            @click=${() => this._switchToMonthView()}
          >${monthNames[grid.month]}</button>
          <button
            class="dp-year-label"
            type="button"
            aria-label="Select year"
            @click=${() => this._switchToYearView()}
          >${grid.year}</button>
        </div>

        <button
          class="dp-nav-btn"
          type="button"
          aria-label="Next month"
          ?hidden=${!showNext}
          @click=${() => this._navigateMonth(1)}
        ><uwc-icon name="chevron-right" size="1.15em"></uwc-icon></button>
      </div>
    `;
  }

  private _renderWeekdays(): TemplateResult {
    const headers = this._cal.getDayHeaders();
    return html`
      <div class="dp-weekdays" role="row">
        ${headers.map(h => html`
          <div class="dp-weekday" role="columnheader" aria-label=${h.long}>${h.label}</div>
        `)}
      </div>
    `;
  }

  private _renderWeeks(grid: MonthGrid): TemplateResult {
    const MONTHS = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
    return html`
      <div
        class="dp-weeks"
        role="grid"
        aria-label="${MONTHS[grid.month]} ${grid.year}"
        @keydown=${this._handleGridKeydown}
      >
        ${grid.weeks.map(week => html`
          <div class="dp-week" role="row">
            ${week.map(cell => this._renderDayCell(cell))}
          </div>
        `)}
      </div>
    `;
  }

  private _renderDayCell(cell: CalendarCell): TemplateResult {
    const sel        = this._sel;
    const selected   = sel.isSelected(cell.date);
    const rangeStart = sel.isRangeStart(cell.date);
    const rangeEnd   = sel.isRangeEnd(cell.date);
    const inRange    = sel.isInRange(cell.date);
    const hoverRange = sel.isHoverRange(cell.date);
    const isFocused  = this._isFocusAnchor(cell.date);

    return html`
      <button
        class=${classMap({
          'dp-day':              true,
          'dp-day--today':       cell.today,
          'dp-day--selected':    selected && !rangeStart && !rangeEnd,
          'dp-day--range-start': rangeStart,
          'dp-day--range-end':   rangeEnd,
          'dp-day--in-range':    inRange,
          'dp-day--hover-range': hoverRange,
          'dp-day--other-month': cell.otherMonth,
          'dp-day--disabled':    cell.disabled,
        })}
        part="day-cell"
        type="button"
        role="gridcell"
        tabindex=${isFocused && !cell.disabled ? 0 : -1}
        ?data-dp-focused=${isFocused && !cell.disabled}
        aria-selected=${selected || rangeStart || rangeEnd}
        aria-disabled=${cell.disabled}
        aria-current=${cell.today ? 'date' : nothing}
        aria-label=${cell.date.toLocaleDateString(this.locale, { year: 'numeric', month: 'long', day: 'numeric' })}
        ?disabled=${cell.disabled}
        @click=${(e: Event) => this._onDateClick(cell, e)}
        @focus=${() => { this._cal.focusedDate = cell.date; }}
        @pointerenter=${() => this._sel.handleHover(cell.date)}
        @pointerleave=${() => this._sel.handleHover(null)}
      >
        ${this.renderDateCell
          ? this.renderDateCell(cell.date)
          : html`<span>${cell.dayNum}</span>`}
      </button>
    `;
  }

  // ── Month picker view ─────────────────────────────────────────────

  private _renderMonthPicker(): TemplateResult {
    const rows = this._cal.getMonthPickerRows();
    return html`
      <div class="dp-cal">
        <div class="dp-header" part="header">
          <button class="dp-nav-btn" type="button" aria-label="Previous year"
            @click=${() => this._cal.navigateYear(-1)}>
            <uwc-icon name="chevron-left" size="1.15em"></uwc-icon>
          </button>
          <div class="dp-header-title" aria-live="polite">
            <button class="dp-year-label" type="button"
              aria-label="Select year"
              @click=${() => this._switchToYearView()}>
              ${this._cal.viewYear}
            </button>
          </div>
          <button class="dp-nav-btn" type="button" aria-label="Next year"
            @click=${() => this._cal.navigateYear(1)}>
            <uwc-icon name="chevron-right" size="1.15em"></uwc-icon>
          </button>
        </div>
        <div class="dp-month-grid" role="grid" aria-label="Month picker">
          ${rows.map(row => row.map(cell => html`
            <button
              class=${classMap({
                'dp-picker-cell':            true,
                'dp-picker-cell--selected':  this._isMonthSelected(cell.value),
                'dp-picker-cell--current':   this._isCurrentMonth(cell.value),
                'dp-picker-cell--disabled':  cell.disabled,
              })}
              type="button"
              role="gridcell"
              aria-selected=${this._isMonthSelected(cell.value)}
              aria-disabled=${cell.disabled}
              aria-current=${this._isCurrentMonth(cell.value) ? 'date' : nothing}
              ?disabled=${cell.disabled}
              @click=${() => this._onMonthClick(cell.value)}
            >${cell.label}</button>
          `))}
        </div>
      </div>
    `;
  }

  // ── Year picker view ──────────────────────────────────────────────

  private _renderYearPicker(): TemplateResult {
    const rows = this._cal.getYearPickerRows(this._yearBase);
    return html`
      <div class="dp-cal">
        <div class="dp-header" part="header">
          <button class="dp-nav-btn" type="button" aria-label="Previous decade"
            @click=${() => { this._yearBase -= 10; this.requestUpdate(); }}>
            <uwc-icon name="chevron-left" size="1.15em"></uwc-icon>
          </button>
          <div class="dp-header-title" aria-live="polite">
            <span class="dp-month-label">${this._yearBase} – ${this._yearBase + 9}</span>
          </div>
          <button class="dp-nav-btn" type="button" aria-label="Next decade"
            @click=${() => { this._yearBase += 10; this.requestUpdate(); }}>
            <uwc-icon name="chevron-right" size="1.15em"></uwc-icon>
          </button>
        </div>
        <div class="dp-year-grid" role="grid" aria-label="Year picker">
          ${rows.map(row => row.map(cell => html`
            <button
              class=${classMap({
                'dp-picker-cell':           true,
                'dp-picker-cell--selected': this._isYearSelected(cell.value),
                'dp-picker-cell--current':  this._isCurrentYear(cell.value),
                'dp-picker-cell--disabled': cell.disabled,
              })}
              type="button"
              role="gridcell"
              aria-selected=${this._isYearSelected(cell.value)}
              aria-disabled=${cell.disabled}
              aria-current=${this._isCurrentYear(cell.value) ? 'date' : nothing}
              ?disabled=${cell.disabled}
              @click=${() => this._onYearClick(cell.value)}
            >${cell.label}</button>
          `))}
        </div>
      </div>
    `;
  }

  // ── Button bar ────────────────────────────────────────────────────

  private _renderButtonBar(): TemplateResult {
    return html`
      <div class="dp-button-bar">
        <uwc-button
          label="Today"
          variant="secondary"
          size="small"
          text
          @uwc-click=${() => this._onTodayClick()}
        ></uwc-button>
        <uwc-button
          label="Clear"
          variant="secondary"
          size="small"
          text
          @uwc-click=${() => this._onClearClick()}
        ></uwc-button>
      </div>
    `;
  }

  // ── Time picker ───────────────────────────────────────────────────

  private _applyTimeToValue(): void {
    let base: Date | null = null;

    if (this.timeOnly) {
      const existing = this._sel.value as DatePickerSingleValue;
      if (existing instanceof Date) {
        base = new Date(existing.getFullYear(), existing.getMonth(), existing.getDate());
      } else {
        const now = new Date();
        base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
    } else if (this._sel.hasValue()) {
      const existing = this._sel.value as DatePickerSingleValue;
      if (existing instanceof Date) base = existing;
    }

    if (!base) return;

    const withTime = this._time.applyToDate(base);
    this._sel.syncFromProp(withTime);
    this.value = this._sel.value;
    this._syncInputText();
    this._internals.setFormValue(this._sel.getFormValue());
    emit(this, 'uwc-date-select', {
      value: this.value,
      selectionMode: this.selectionMode,
      originalEvent: null,
    });
  }

  private _renderTimePicker(): TemplateResult {
    const pad = (n: number) => String(n).padStart(2, '0');
    const displayH = this.hourFormat === '12' ? this._time.displayHour : this._time.hour;

    return html`
      <div class="dp-time" role="group" aria-label="Time picker">
        ${this._renderTimeCol(
          pad(displayH),
          () => { this._time.stepHour(1);   this._applyTimeToValue(); },
          () => { this._time.stepHour(-1);  this._applyTimeToValue(); },
          'Hours',
        )}
        <span class="dp-time-sep" aria-hidden="true">:</span>
        ${this._renderTimeCol(
          pad(this._time.minute),
          () => { this._time.stepMinute(1);  this._applyTimeToValue(); },
          () => { this._time.stepMinute(-1); this._applyTimeToValue(); },
          'Minutes',
        )}
        ${this.showSeconds ? html`
          <span class="dp-time-sep" aria-hidden="true">:</span>
          ${this._renderTimeCol(
            pad(this._time.second),
            () => { this._time.stepSecond(1);  this._applyTimeToValue(); },
            () => { this._time.stepSecond(-1); this._applyTimeToValue(); },
            'Seconds',
          )}
        ` : nothing}
        ${this.hourFormat === '12' ? html`
          <button
            class="dp-time-ampm"
            type="button"
            aria-label="Toggle AM/PM, currently ${this._time.meridiem}"
            @click=${() => { this._time.toggleMeridiem(); this._applyTimeToValue(); }}
          >${this._time.meridiem}</button>
        ` : nothing}
      </div>
    `;
  }

  private _renderTimeCol(
    value: string,
    onUp: () => void,
    onDown: () => void,
    label: string,
  ): TemplateResult {
    return html`
      <div class="dp-time-col" role="group" aria-label="${label}">
        <button class="dp-time-btn" type="button" aria-label="Increment ${label}"
          @click=${onUp}>
          <uwc-icon name="chevron-up" size="1em"></uwc-icon>
        </button>
        <span class="dp-time-val" aria-live="polite">${value}</span>
        <button class="dp-time-btn" type="button" aria-label="Decrement ${label}"
          @click=${onDown}>
          <uwc-icon name="chevron-down" size="1em"></uwc-icon>
        </button>
      </div>
    `;
  }

  // ── Selection state helpers ───────────────────────────────────────

  private _isMonthSelected(month: number): boolean {
    const v = this.value as DatePickerSingleValue;
    if (!v) return false;
    return v instanceof Date && v.getMonth() === month && v.getFullYear() === this._cal.viewYear;
  }

  private _isYearSelected(year: number): boolean {
    const v = this.value as DatePickerSingleValue;
    if (!v) return false;
    return v instanceof Date && v.getFullYear() === year;
  }

  private _isCurrentMonth(month: number): boolean {
    const now = new Date();
    return now.getMonth() === month && now.getFullYear() === this._cal.viewYear;
  }

  private _isCurrentYear(year: number): boolean {
    return new Date().getFullYear() === year;
  }

  // ── Roving tabindex / keyboard nav helpers ────────────────────────

  private _isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth()    === b.getMonth()
        && a.getDate()     === b.getDate();
  }

  private _getInitialFocusDate(): Date {
    const sel = this._sel.value;
    if (sel instanceof Date) return sel;
    const today = new Date();
    const grids = this._cal.getMonthGrids();
    for (const grid of grids) {
      if (grid.year === today.getFullYear() && grid.month === today.getMonth()) return today;
    }
    return new Date(this._cal.viewYear, this._cal.viewMonth, 1);
  }

  private _isFocusAnchor(date: Date): boolean {
    const anchor = this._cal.focusedDate ?? this._getInitialFocusDate();
    return this._isSameDay(anchor, date);
  }

  private _focusActiveCell(): void {
    this._focusCellEl?.focus({ preventScroll: true });
  }

  private _handleGridKeydown = (e: KeyboardEvent): void => {
    const fallback = this._getInitialFocusDate();
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this._cal.moveFocus('left', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'ArrowRight':
        e.preventDefault();
        this._cal.moveFocus('right', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._cal.moveFocus('up', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._cal.moveFocus('down', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'Home':
        e.preventDefault();
        this._cal.moveFocus('home', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'End':
        e.preventDefault();
        this._cal.moveFocus('end', fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'PageUp':
        e.preventDefault();
        this._cal.moveFocusMonth(-1, fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'PageDown':
        e.preventDefault();
        this._cal.moveFocusMonth(1, fallback);
        this.updateComplete.then(() => this._focusActiveCell());
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const fd = this._cal.focusedDate;
        if (!fd) break;
        for (const grid of this._cal.getMonthGrids()) {
          for (const week of grid.weeks) {
            for (const cell of week) {
              if (this._isSameDay(cell.date, fd) && !cell.disabled) {
                this._onDateClick(cell, e);
                return;
              }
            }
          }
        }
        break;
      }
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'uwc-datepicker': UwcDatepicker;
  }
}
